import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDownTrayIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import FileDropzone, { VALID_FILE_TYPES } from '../FileDropzone';
import documentsService, { TablemaintDocumentRow } from '../../services/tablemaintDocuments.service';
import { saveAs } from '../../utils/saveFile';

/**
 * Documents panel for a table-maintenance drawer. Single document type per
 * entity; upload/list/preview/download/delete against `${basePath}/{recno}/documents`.
 * Shared by Bill Tos (/billtos, "INVOICING A/P QUESTIONNAIRE") and Customers
 * (/customers-maint, "SOP").
 */
const errMsg = (e: unknown, fallback: string): string => {
  const r = (e as any)?.response;
  const d = r?.data?.detail;
  return r?.status >= 400 && r?.status < 500 && typeof d === 'string' && d.trim() ? d : fallback;
};

const DocumentsPanel: React.FC<{ recno: number; basePath: string; docType: string }> = ({
  recno, basePath, docType,
}) => {
  const [rows, setRows] = useState<TablemaintDocumentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const descRef = useRef(description);
  useEffect(() => { descRef.current = description; }, [description]);

  const refresh = useCallback(() => {
    documentsService.list(basePath, recno)
      .then(setRows)
      .catch((e) => setError(errMsg(e, 'Failed to load documents.')));
  }, [basePath, recno]);

  useEffect(() => { refresh(); }, [refresh]);

  const onUpload = useCallback(
    (file: File): Promise<TablemaintDocumentRow | null> => {
      setError(null);
      return documentsService
        .upload(basePath, recno, file, { documenttype: docType, description: descRef.current || undefined })
        .catch((e) => {
          setError(errMsg(e, `Upload failed for ${file.name}.`));
          throw e;
        });
    },
    [basePath, recno, docType],
  );

  const preview = async (row: TablemaintDocumentRow) => {
    try {
      const blob = await documentsService.previewBlob(basePath, recno, row.mapRecno);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(errMsg(e, `Failed to preview ${row.filename}.`));
    }
  };

  const download = async (row: TablemaintDocumentRow) => {
    try {
      await saveAs(row.filename || 'document', () =>
        documentsService.downloadBlob(basePath, recno, row.mapRecno),
      );
    } catch (e) {
      setError(errMsg(e, `Failed to download ${row.filename}.`));
    }
  };

  const remove = async (row: TablemaintDocumentRow) => {
    try {
      await documentsService.remove(basePath, recno, row.mapRecno);
      refresh();
    } catch (e) {
      setError(errMsg(e, `Failed to delete ${row.filename}.`));
    }
  };

  return (
    <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <legend className="px-2 text-sm font-semibold text-label">
        Documents <span className="font-normal text-gray-400">· {docType}</span>
      </legend>

      {rows.length > 0 && (
        <table className="w-full text-sm mb-3">
          <thead>
            <tr className="text-left text-muted">
              <th className="py-1 font-medium">File</th>
              <th className="py-1 font-medium">Description</th>
              <th className="py-1 font-medium">Uploaded</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.mapRecno} className="text-gray-800 dark:text-gray-200">
                <td className="py-1 font-medium">{row.filename || '—'}</td>
                <td className="py-1 max-w-[220px] truncate" title={row.description}>
                  {row.description || '—'}
                </td>
                <td className="py-1 text-xs text-muted whitespace-nowrap">
                  {row.uploadDatetime || '—'}
                </td>
                <td className="py-1 text-right whitespace-nowrap">
                  <div className="inline-flex gap-1.5">
                    <button type="button" onClick={() => preview(row)} title="Preview"
                      className="p-1 text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400">
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => download(row)} title="Download"
                      className="p-1 text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400">
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => remove(row)} title="Delete"
                      className="p-1 text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        className="mb-2 w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
      />
      <FileDropzone<TablemaintDocumentRow> onUpload={onUpload} onUploaded={() => refresh()} />
      <div className="mt-1 text-xs text-gray-400">Valid file types: {VALID_FILE_TYPES.join(', ')}</div>
      {error && <div className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</div>}
    </fieldset>
  );
};

export default DocumentsPanel;
